import React, { useEffect, useMemo, useState } from "react";
import { MessageSquare, Filter } from "lucide-react";
import { apiGet } from "../../../Api/Client.js";
import { stamp } from "./CallPlayer.jsx";

// The transcript, with every scored moment pinned to the line it was made on.
//
// This is the view that makes a score arguable in the right way. A number on a
// radar invites "says who"; the same number sitting against the sentence that
// produced it, with the two lines either side of it, is something a coach and a
// rep can actually look at together. Reading the line BEFORE the quote is most
// of what a 1:1 is — which is why this shows the whole call rather than
// extracting the quoted fragments into a list.
//
// Three things it is careful about:
//
//   * The rep's lines are marked from the recorder's speaker roster, never
//     from who talks most. If attribution was not confirmed, it says so
//     instead of colouring lines in.
//   * UNATTRIBUTED_SPEECH stays unattributed and is visibly marked, because it
//     is excluded from every ratio upstream and the transcript should show why
//     a percentage does not cover the whole call.
//   * A gap in the timeline is left as a gap. Real recordings drop audio; the
//     Rinky call has 37 seconds missing in the middle. Nothing here invents
//     what happened there.

const muted = { color: "var(--c-500)" };

function MomentTag({ moment }) {
  const strength = moment.kind === "strength";
  const color = strength ? "var(--green-600)" : "var(--blue-600)";
  return (
    <div style={{
      margin: "6px 0 8px 0", padding: "8px 10px", borderRadius: 8,
      background: "var(--c-100, rgba(120,120,120,.07))",
      borderLeft: `3px solid ${color}`,
    }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".05em",
                       textTransform: "uppercase", color }}>
          {strength ? "worked" : "to fix"}
        </span>
        <strong style={{ fontSize: 12, color: "var(--c-900)" }}>{moment.label}</strong>
        <span style={{ fontSize: 12, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>
          {moment.score}/10
        </span>
      </div>
      {moment.why && (
        <div style={{ fontSize: 12, color: "var(--c-700)", marginTop: 3, lineHeight: 1.5 }}>
          {moment.why}
        </div>
      )}
      {moment.try_next && (
        <div style={{ fontSize: 12, color: "var(--c-800)", marginTop: 4 }}>
          → {moment.try_next}
        </div>
      )}
      {/* Only the `ten` anchor, ever. The `zero` anchors are written to define
          a standard and read as an insult attached to a person. */}
      {!strength && moment.ten && (
        <div style={{ fontSize: 11.5, ...muted, marginTop: 4 }}>
          A 10 sounds like: {moment.ten}
        </div>
      )}
    </div>
  );
}

function Line({ segment, prev }) {
  const { is_rep: isRep, unattributed } = segment;
  // A hole in the timeline is shown as a hole. The alternative is a transcript
  // that reads as continuous when thirty-seven seconds of it are missing.
  const gap = prev ? segment.start - prev.end : 0;

  return (
    <>
      {gap > 5 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "10px 0",
                      fontSize: 10.5, ...muted }}>
          <span style={{ flex: 1, height: 1, background: "var(--c-200)" }} />
          {Math.round(gap)}s not captured
          <span style={{ flex: 1, height: 1, background: "var(--c-200)" }} />
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "46px 1fr", gap: 10, padding: "3px 0" }}>
        <div style={{ fontSize: 11, ...muted, fontVariantNumeric: "tabular-nums", paddingTop: 2 }}>
          {stamp(segment.start)}
        </div>
        <div>
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".04em",
                        textTransform: "uppercase",
                        color: unattributed ? "var(--c-400)"
                          : isRep ? "var(--blue-600)" : "var(--c-600)" }}>
            {unattributed ? "unidentified speaker" : isRep ? "rep" : "buyer"}
          </div>
          <div style={{
            fontSize: 13, lineHeight: 1.6,
            color: unattributed ? "var(--c-500)" : "var(--c-800)",
            fontStyle: unattributed ? "italic" : "normal",
          }}>
            {segment.text}
          </div>
          {segment.moments.map((m) => <MomentTag key={m.key} moment={m} />)}
        </div>
      </div>
    </>
  );
}

export default function TranscriptAnalyser({ callId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [onlyScored, setOnlyScored] = useState(false);

  useEffect(() => {
    if (!callId) return undefined;
    let cancelled = false;
    setData(null); setError("");
    apiGet(`/RevOps/SellerCallScores/${callId}/transcript/`)
      .then((payload) => !cancelled && setData(payload))
      .catch((err) => !cancelled && setError(err?.message || "Couldn't load the transcript."));
    return () => { cancelled = true; };
  }, [callId]);

  const shown = useMemo(() => {
    if (!data) return [];
    if (!onlyScored) return data.segments;
    // Keep the line before and after each scored moment: a quote with no
    // context is exactly the thing this view exists to stop.
    const keep = new Set();
    data.segments.forEach((s, i) => {
      if (s.moments.length) [i - 1, i, i + 1].forEach((j) => keep.add(j));
    });
    return data.segments.filter((_, i) => keep.has(i));
  }, [data, onlyScored]);

  if (error) return <div className="Login-Error">{error}</div>;
  if (!data) return <p style={muted}>Loading transcript…</p>;

  if (!data.available) {
    return (
      <p style={{ ...muted, fontSize: 12.5, margin: 0 }}>
        No transcript stored for this call. Calls scored before the transcript was kept
        alongside the scores will show this — the scores themselves are unaffected.
      </p>
    );
  }

  const talk = data.measures?.talk_to_listen;
  const unattributedShare = data.measures?._confidence?.unattributed_share;

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center",
                    justifyContent: "space-between", marginBottom: 10 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5,
                       fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
                       color: "var(--c-600)" }}>
          <MessageSquare size={13} /> Transcript · {data.moments.length} scored moment
          {data.moments.length === 1 ? "" : "s"}
        </span>
        <button onClick={() => setOnlyScored((v) => !v)}
                style={{ display: "inline-flex", alignItems: "center", gap: 5,
                         border: "1px solid var(--c-200)", background: onlyScored
                           ? "var(--c-100, rgba(120,120,120,.12))" : "transparent",
                         borderRadius: 8, padding: "4px 9px", cursor: "pointer",
                         fontSize: 11.5, color: "var(--c-700)" }}>
          <Filter size={11} /> {onlyScored ? "Showing scored moments" : "Show whole call"}
        </button>
      </div>

      <div style={{ fontSize: 11, ...muted, marginBottom: 10 }}>
        {data.rep_attribution === "human_confirmed" || data.rep_attribution === "matched"
          ? `Rep identified from the recording's speaker roster (${data.rep_attribution.replace("_", " ")}).`
          : "The rep's speaker could not be confirmed on this recording, so per-speaker figures are withheld."}
        {typeof talk === "number" && ` Rep spoke for ${Math.round(talk * 100)}% of the attributed time.`}
        {unattributedShare > 0 &&
          ` ${Math.round(unattributedShare * 100)}% of the call could not be attributed and is excluded from that.`}
      </div>

      <div style={{ maxHeight: 520, overflowY: "auto", paddingRight: 6 }}>
        {shown.map((s, i) => <Line key={s.index} segment={s} prev={i ? shown[i - 1] : null} />)}
      </div>

      {/* A scored moment whose timestamp lands in a part of the recording that
          was never captured. It cannot be shown against a line, because there
          is no line — but dropping it would leave a score on the card that the
          reviewer can read and never locate, which is worse than saying so. */}
      {(data.unplaced_moments || []).length > 0 && (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--c-200)" }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".04em",
                        textTransform: "uppercase", color: "var(--c-500)" }}>
            Scored where the recording has a gap
          </div>
          {data.unplaced_moments.map((m) => (
            <div key={m.key} style={{ marginTop: 6 }}>
              <div style={{ fontSize: 12, color: "var(--c-800)" }}>
                <strong>{m.label}</strong>
                {m.score != null && <span style={{ marginLeft: 6 }}>{m.score}/10</span>}
                <span style={{ ...muted, marginLeft: 6 }}>at {stamp(m.at_seconds)}</span>
              </div>
              {m.why && (
                <div style={{ fontSize: 11.5, ...muted, marginTop: 2 }}>{m.why}</div>
              )}
            </div>
          ))}
          <p style={{ fontSize: 11, ...muted, margin: "6px 0 0" }}>
            The transcript has no words at these moments, so there is no line to show them against.
          </p>
        </div>
      )}
    </div>
  );
}
